import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { NestApi } from '../api/axiosInstance';
import { validatePdfFileBytes } from '../../utils/fileValidation';

interface Job {
  job_id: number;
  [key: string]: any;
}

// ── NestJS DTO types ──────────────────────────────────────────────────────────

export interface CreateJobPayload {
  job_title: string;
  job_description?: string;
  no_of_openings?: number;
  company_name?: string;
  company_id?: number;
  job_type?: string;
  JobCategory_id?: number;
  job_location?: string;
  min_experience?: number;
  max_experience?: number;
  min_salary?: number;
  max_salary?: number;
  salary_type?: 'Monthly' | 'Yearly' | 'Hourly';
  currency?: string;
  education_qualification?: string;
  education_specialization?: string;
  skills?: string;
  city?: string;
  state?: string;
  country?: string;
  full_address?: string;
  address?: string;
  postal_code?: string;
  benefits?: string;
  summery?: string;
  isInternal?: boolean;
  Owner_name?: string;
  collaborator?: string;
}

export interface JobFilterParams {
  page?: number;
  limit?: number;
  job_title?: string;
  job_type?: string;
  job_status?: string;
  job_category?: string;
  city?: string;
  min_experience?: number;
  max_experience?: number;
  min_salary?: number;
  max_salary?: number;
  candidate_id?: number;
}


export interface MatchingState {
  status: 'idle' | 'running' | 'completed';
  started_at: string | null;
  completed_at: string | null;
  triggered_by: string | null;
  total_found: number | null;
  lock_expires_at: string | null;
}

interface JobState {
  jobs: readonly Job[];
  jobDetails: Job | null;
  jobForms: any[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  limit: number;
  loading: boolean;
  error: string | null;
  success: boolean;
  candidates: any[];
  candidatesLoading: boolean;
  candidatesError: string | null;
  aiGeneratedData: any;
  aiGenerating: boolean;
  aiGenerateError: string | null;
  matchedCandidates: any[];
  matchedLoading: boolean;
  matchedError: string | null;
  matchingState: MatchingState | null;
  refreshLoading: boolean;
  refreshError: string | null;
  cancelLoading: boolean;
  detailLoading: boolean;
  detailError: string | null;
}
// ── NestJS: GET /jobs ─────────────────────────────────────────────────────────
export const fetchJobsList = createAsyncThunk<
  {
    jobs: Job[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
    limit: number;
  },
  JobFilterParams,
  { rejectValue: string }
>(
  'job/fetchJobsList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 10, ...rest } = params;
      const queryParams: Record<string, any> = { page, limit };
      Object.entries(rest).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams[key] = value;
        }
      });
      const response = await NestApi.get('/jobs', { params: queryParams });
      const { data, meta } = response.data;
      return {
        jobs: data,
        totalCount: meta.total,
        currentPage: meta.page,
        totalPages: meta.totalPages,
        limit: meta.limit,
      };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || 'Failed to fetch jobs',
      );
    }
  },
);

// ── NestJS: POST /jobs ────────────────────────────────────────────────────────
export const createJob = createAsyncThunk<
  { message: string; job_id: number },
  CreateJobPayload,
  { rejectValue: string }
>(
  'job/createJob',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await NestApi.post('/jobs', payload);
      return response.data as { message: string; job_id: number };
    } catch (err: any) {
      // Re-throw conflict so callers can detect job_id of duplicate
      if (err.response?.status === 409) {
        return rejectWithValue(JSON.stringify(err.response.data));
      }
      return rejectWithValue(
        err.response?.data?.message?.[0] ||
        err.response?.data?.message ||
        err.response?.data ||
        'Failed to create job',
      );
    }
  },
);

export const fetchJobById = createAsyncThunk<Job, number, { rejectValue: string }>(
  'job/fetchJobById',
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await NestApi.get(`/jobs/${jobId}`);
      // API wraps the job in { success, statusCode, data: {...}, timestamp }
      return (response.data?.data ?? response.data) as Job;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch job');
    }
  }
);

// ── NestJS: POST /jobs/create-via-ai ────────────────────────────────────────
// Sends a free-form prompt to the AI, returns generated job data for form
// pre-fill. Does NOT persist a job — the user reviews then submits via createJob.
export const generateJobWithAI = createAsyncThunk<
  { message: string; aiGeneratedPost: Record<string, any> },
  { prompt: string },
  { rejectValue: string }
>(
  'job/generateJobWithAI',
  async ({ prompt }, { rejectWithValue }) => {
    try {
      const response = await NestApi.post('/jobs/create-via-ai', { prompt });
      // Unwrap NestJS transform interceptor: { success, statusCode, data: {...} }
      return response.data?.data ?? response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data?.error || 'Failed to generate job with AI',
      );
    }
  },
);

// NestJS: POST /jobs/upload-jd
export const createJobViafile = createAsyncThunk<
  { message: string; results: any[]; summary: Record<string, number> },
  File,
  { rejectValue: string }
>('job/createJobViafile', async (file, { rejectWithValue }) => {
  try {
    const validation = await validatePdfFileBytes(file);
    if (validation !== true) {
      return rejectWithValue(validation);
    }

    const formData = new FormData();
    formData.append('jobDescription', file);

    const response = await NestApi.post('/jobs/upload-jd', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000, // 10 min — AI processing can be slow
    });

    // NestJS TransformInterceptor wraps response: { success, statusCode, data: { message, results, summary } }
    // Unwrap to return just the inner data object so callers can access .summary directly
    return response.data?.data ?? response.data;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || err.response?.data?.error || 'Failed to upload JD file',
    );
  }
});
// Update job
export const updateJob = createAsyncThunk<
  { jobId: number; updatedData: Record<string, any> },
  { jobId: number; updatedData: Record<string, any> },
  { rejectValue: string }
>('job/updateJob', async ({ jobId, updatedData }, { rejectWithValue }) => {
  try {
    await NestApi.put(`/jobs/${jobId}`, updatedData);
    // Return the args so the reducer can merge them into the local state
    // without a full list re-fetch.
    return { jobId, updatedData };
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message ||
      err.response?.data?.error ||
      'Failed to update job',
    );
  }
});

// Delete job
export const deleteJob = createAsyncThunk<
  { jobId: number },
  number,
  { rejectValue: string }
>('job/deleteJob', async (jobId, { rejectWithValue }) => {
  try {
    await NestApi.delete(`/jobs/${jobId}`);
    return { jobId };
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message ||
      err.response?.data?.error ||
      'Failed to delete job',
    );
  }
});

export const fetchCandidatesFilters = createAsyncThunk<
  any,
  void,
  { rejectValue: string }
>('job/fetchCandidatesFilters', async (_, { rejectWithValue }) => {
  try {
    const response = await NestApi.get('/jobs/filters/candidates');
    return response.data?.data ?? response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || 'Failed to fetch candidates filters');
  }
});

export const fetchCandidatesByJobId = createAsyncThunk(
  'job/fetchCandidatesByJobId',
  async (jobId: number, { rejectWithValue }) => {
    try {
      const res = await NestApi.get(`/jobs/${jobId}/candidates`);
      const data = res.data?.data ?? res.data;
      return Array.isArray(data) ? data : [];
    } catch (error: any) {
      if (error.response?.status === 404) {
        return [];
      }
      return rejectWithValue('Failed to fetch candidates');
    }
  }
);


export const fetchMatchedCandidates = createAsyncThunk(
  'job/fetchMatchedCandidates',
  async (jobId: number, { rejectWithValue }) => {
    try {
      const res = await NestApi.get(`/jobs/${jobId}/matched-candidates`);
      const body = res.data?.data ?? res.data;
      // API now returns { candidates, matchingState }
      if (body && typeof body === 'object' && 'candidates' in body) {
        return body as { candidates: any[]; matchingState: MatchingState };
      }
      // Fallback: old shape was a plain array
      return { candidates: Array.isArray(body) ? body : [], matchingState: null };
    } catch (error: any) {
      if (error.response?.status === 404) {
        return { candidates: [], matchingState: null };
      }
      return rejectWithValue('Failed to fetch matched candidates');
    }
  }
);

export const refreshMatchedCandidates = createAsyncThunk(
  'job/refreshMatchedCandidates',
  async (jobId: number, { rejectWithValue }) => {
    try {
      const res = await NestApi.post(`/jobs/${jobId}/matched-candidates/refresh`);
      return (res.data?.data ?? res.data) as { status: string; message: string };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to start scan'
      );
    }
  }
);

export const cancelMatchingScan = createAsyncThunk(
  'job/cancelMatchingScan',
  async (jobId: number, { rejectWithValue }) => {
    try {
      const res = await NestApi.post(`/jobs/${jobId}/matched-candidates/cancel`);
      return (res.data?.data ?? res.data) as { message: string };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to cancel scan'
      );
    }
  }
);

// Initial state
const initialState: JobState = {
  jobs: [],
  jobDetails: null,
  jobForms: [],
  totalCount: 0,
  currentPage: 1,
  totalPages: 1,
  limit: 10,
  loading: false,
  error: null,
  success: false,
  candidates: [],
  candidatesLoading: false,
  candidatesError: null,
  aiGeneratedData: null,
  aiGenerating: false,
  aiGenerateError: null,
  matchedCandidates: [],
  matchedLoading: false,
  matchedError: null,
  matchingState: null,
  refreshLoading: false,
  refreshError: null,
  cancelLoading: false,
  detailLoading: false,
  detailError: null,
};

const jobSlice = createSlice({
  name: 'job',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobsList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobsList.fulfilled, (state, action) => {
        state.loading = false;
        state.jobs = action.payload.jobs;
        state.totalCount = action.payload.totalCount;
        state.currentPage = action.payload.currentPage;
        state.totalPages = action.payload.totalPages;
        state.limit = action.payload.limit;
      })
      .addCase(fetchJobsList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch jobs';
      })

      .addCase(fetchJobById.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchJobById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.jobDetails = action.payload;
      })
      .addCase(fetchJobById.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload || 'Fetch job failed';
      })

      .addCase(createJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createJob.fulfilled, (state, action) => {
        state.loading = false;
        // Re-fetch will populate full details; optimistically bump count
        state.totalCount += 1;
      })
      .addCase(createJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create job';
      })

      .addCase(createJobViafile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createJobViafile.fulfilled, (state) => {
        // Response is { message, results, summary } — individual jobs are
        // fetched fresh via fetchJobsList after this action resolves.
        state.loading = false;
      })
      .addCase(createJobViafile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'File upload failed';
      })

      .addCase(updateJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateJob.fulfilled, (state, action) => {
        state.loading = false;
        const { jobId, updatedData } = action.payload;
        const index = state.jobs.findIndex((j) => j.job_id === jobId);
        if (index !== -1) {
          // Merge only the fields the caller changed; keep existing
          // JOIN-derived fields like company_name, AddedBy intact.
          state.jobs[index] = { ...state.jobs[index], ...updatedData };
        }
      })
      .addCase(updateJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Update failed';
      })

      .addCase(deleteJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteJob.fulfilled, (state, action) => {
        state.loading = false;
        state.jobs = state.jobs.filter((j) => j.job_id !== action.payload.jobId);
        state.totalCount -= 1;
      })
      .addCase(deleteJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Delete failed';
      })

      .addCase(generateJobWithAI.pending, (state) => {
        state.aiGenerating = true;
        state.aiGenerateError = null;
        state.aiGeneratedData = null;
      })
      .addCase(generateJobWithAI.fulfilled, (state, action) => {
        state.aiGenerating = false;
        state.aiGeneratedData = action.payload;
      })
      .addCase(generateJobWithAI.rejected, (state, action) => {
        state.aiGenerating = false;
        state.aiGenerateError = action.payload || 'Failed to generate job with AI';
      })

      .addCase(fetchCandidatesByJobId.pending, (state) => {
        state.candidatesLoading = true;
        state.candidatesError = null;
        state.candidates = [];
      })
      .addCase(fetchCandidatesByJobId.fulfilled, (state, action) => {
        state.candidatesLoading = false;
        state.candidates = action.payload;
      })
      .addCase(fetchCandidatesByJobId.rejected, (state, action) => {
        state.candidatesLoading = false;
        state.candidatesError = action.payload as string;
      })

      .addCase(fetchMatchedCandidates.pending, (state) => {
        state.matchedLoading = true;
        state.matchedError = null;
      })
      .addCase(fetchMatchedCandidates.fulfilled, (state, action) => {
        state.matchedLoading = false;
        state.matchedCandidates = action.payload.candidates;
        state.matchingState = action.payload.matchingState;
      })
      .addCase(fetchMatchedCandidates.rejected, (state, action) => {
        state.matchedLoading = false;
        state.matchedError = action.payload as string;
      })

      .addCase(refreshMatchedCandidates.pending, (state) => {
        state.refreshLoading = true;
        state.refreshError = null;
      })
      .addCase(refreshMatchedCandidates.fulfilled, (state) => {
        state.refreshLoading = false;
        // Optimistically mark as running — next GET will confirm
        if (state.matchingState) {
          state.matchingState = { ...state.matchingState, status: 'running', started_at: new Date().toISOString() };
        } else {
          state.matchingState = { status: 'running', started_at: new Date().toISOString(), completed_at: null, triggered_by: null, total_found: null, lock_expires_at: null };
        }
      })
      .addCase(refreshMatchedCandidates.rejected, (state, action) => {
        state.refreshLoading = false;
        state.refreshError = action.payload as string;
      })

      .addCase(cancelMatchingScan.pending, (state) => {
        state.cancelLoading = true;
      })
      .addCase(cancelMatchingScan.fulfilled, (state) => {
        state.cancelLoading = false;
        if (state.matchingState) {
          state.matchingState = { ...state.matchingState, status: 'idle' };
        }
      })
      .addCase(cancelMatchingScan.rejected, (state) => {
        state.cancelLoading = false;
      })

      ;
  },
});

export default jobSlice.reducer;